import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useInView } from 'react-intersection-observer';
import { Virtuoso } from 'react-virtuoso';
import { gql } from '@soundxyz/gql-string';
import { enumChecker } from '@soundxyz/utils/constants/enums';
import { BondingCurveSaleList } from '@components/Feed/ListViews/BondingCurveSale';
import { PlaylistCreatedList } from '@components/Feed/ListViews/Playlist/PlaylistCreated';
import { PlaylistLikedList } from '@components/Feed/ListViews/Playlist/PlaylistLiked';
import { ReleasesAddedRemovedList } from '@components/Feed/ListViews/Playlist/ReleasesAddedRemoved';
import { PrimarySaleList } from '@components/Feed/ListViews/PrimarySale';
import { ReleaseDroppedList } from '@components/Feed/ListViews/ReleaseDropped';
import { ReleaseLikedList } from '@components/Feed/ListViews/ReleaseLiked';
import { ReleasesAddedList } from '@components/Feed/ListViews/ReleasesAdded';
import { SecondarySaleList } from '@components/Feed/ListViews/SecondarySale';
import { BondingCurveSoldList } from '@components/Feed/ListViews/SoldBondingCurveNft';
import { EmptyState } from '@components/Feed/ListViews/components/EmptyState';
import { FeedListSkeleton } from '@components/Feed/ListViews/components/Skeleton';
import { FeedError } from '@components/Feed/Placeholders';
import type { ArtistActivityFeedFilterArgs, FragmentType } from '@graphql/reactQuery';
import {
  ArtistActivityFeedActivityTypeFilterOption,
  ArtistActivityFeedDocument,
  ArtistActivityFeedTypeFilterOption,
  ArtistActivitySectionArtistInfoFragmentDoc,
  getFragment,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { usePopState } from '@hooks/usePopState';
import { TEMPORARY_SUCCESS_STATE_MS } from '@utils/constants';
import { Refresh } from '../../../Feed/ListViews/components/Refresh';
import { SectionHeader } from '../SectionHeader';
import { ActivityFeed } from '../shared';
import { FeedHeader } from './FeedHeader';
import type { Action } from './store';
import { ArtistActivityState, useArtistActivityState } from './store';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ArtistActivityFeed(
    $artistId: UUID!
    $pagination: CursorConnectionArgs!
    $filter: ArtistActivityFeedFilterArgs
  ) {
    artistActivityFeed(pagination: $pagination, artistId: $artistId, filter: $filter) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          id
          date
          user {
            id
          }
          ... on PrimarySale {
            id
            release {
              id
            }
            ...PrimarySaleList
          }
          ... on SecondarySale {
            id
            release {
              id
            }
            purchasedFromUser {
              id
            }
            ...SecondarySaleList
          }
          ... on BondingCurveSale {
            id
            release {
              id
            }
            ...BondingCurveSaleList
          }
          ... on BondingCurveSold {
            id
            release {
              id
            }
            ...BondingCurveSoldList
          }
          ... on ReleasesAddedToPlaylist {
            id
            playlist {
              id
            }
            ...ReleasesAddedList
          }
          ... on ReleasesAddedRemovedFromPlaylist {
            id
            playlist {
              id
            }
            numAddedReleases
            ...ReleasesAddedRemovedList
          }
          ... on PlaylistCreated {
            id
            playlist {
              id
            }
            ...PlaylistCreatedList
          }
          ... on PlaylistLiked {
            id
            playlist {
              id
            }
            ...PlaylistLikedList
          }
          ... on ReleaseDropped {
            id
            release {
              id
            }
            ...ReleaseDroppedList
          }
          ... on ReleaseLiked {
            id
            release {
              id
            }
            ...ReleaseLikedList
          }
        }
      }
    }
  }

  fragment ArtistActivitySectionArtistInfo on Artist {
    id
    webappUri
  }
`);

export const ActionComponent = ({ item }: { item: Action }) => {
  switch (item.__typename) {
    case 'PrimarySale':
      return <PrimarySaleList key={item.id} activityFrag={item} />;

    case 'BondingCurveSale':
      return <BondingCurveSaleList key={item.id} activityFrag={item} />;

    case 'BondingCurveSold':
      return <BondingCurveSoldList key={item.id} activityFrag={item} />;

    case 'SecondarySale':
      return <SecondarySaleList key={item.id} activityFrag={item} />;

    case 'ReleasesAddedToPlaylist':
      return item.playlist ? (
        <ReleasesAddedList key={item.id} activityFrag={item} playlistFeed={false} />
      ) : null;

    case 'ReleasesAddedRemovedFromPlaylist':
      if (!item.playlist) return null;
      if (item.numAddedReleases > 0)
        return <ReleasesAddedRemovedList key={item.id} activityFrag={item} />;
      return null;

    case 'PlaylistCreated':
      return item.playlist ? (
        <PlaylistCreatedList key={item.id} activityFrag={item} view="artist" />
      ) : null;

    case 'ReleaseDropped':
      return <ReleaseDroppedList key={item.id} activityFrag={item} />;

    case 'ReleaseLiked':
      return item.release ? <ReleaseLikedList key={item.id} activityFrag={item} /> : null;

    case 'PlaylistLiked':
      return item.playlist ? <PlaylistLikedList key={item.id} activityFrag={item} /> : null;

    default:
      return null;
  }
};

const ItemContent = ({
  index,
  item,
  artistId,
  activityFilter,
  userFilter,
}: {
  index: number;
  item: Action;
  artistId: string;
  activityFilter: ArtistActivityFeedActivityTypeFilterOption[];
  userFilter: ArtistActivityFeedTypeFilterOption[];
}) => {
  const { inView, ref } = useInView({ rootMargin: '-45% -5px', threshold: 0.1 });

  useEffect(() => {
    if (inView) {
      ArtistActivityState.scrollIndices[artistId] = index;
    }
    // pass filters to update scroll index when switching filters
  }, [inView, index, artistId, activityFilter, userFilter]);

  return (
    <div ref={ref}>
      <ActionComponent item={item} />
    </div>
  );
};

const renderItemContent = ({
  index,
  item,
  artistId,
  activityFilter,
  userFilter,
}: {
  index: number;
  item: Action;
  artistId: string;
  activityFilter: ArtistActivityFeedActivityTypeFilterOption[];
  userFilter: ArtistActivityFeedTypeFilterOption[];
}) => (
  <ItemContent
    index={index}
    item={item}
    artistId={artistId}
    activityFilter={activityFilter}
    userFilter={userFilter}
  />
);

const isArtistActivityFeedActivityTypeFilterOption = enumChecker(
  ArtistActivityFeedActivityTypeFilterOption,
);
const isArtistActivityFeedTypeFilterOption = enumChecker(ArtistActivityFeedTypeFilterOption);
export const ArtistActivitySection = ({
  artist,
}: {
  artist: FragmentType<ArtistActivitySectionArtistInfoFragmentDoc>;
}) => {
  const [showRefreshSuccess, setShowRerefreshSuccess] = useState(false);

  const snapshot = useArtistActivityState();
  const { isPopStateEvent } = usePopState();

  const { id: artistId, webappUri } = getFragment(
    ArtistActivitySectionArtistInfoFragmentDoc,
    artist,
  );

  const memoizedActivityFilter = useMemo(
    () =>
      snapshot.activityFilter[artistId]?.filter(isArtistActivityFeedActivityTypeFilterOption) || [],
    [snapshot.activityFilter, artistId],
  );

  const memoizedUserFilter = useMemo(
    () => snapshot.userFilter[artistId]?.filter(isArtistActivityFeedTypeFilterOption) || [],
    [snapshot.userFilter, artistId],
  );

  const setActivityFilter = useCallback(
    (selection: ArtistActivityFeedActivityTypeFilterOption[]) => {
      if (isEqual(snapshot.activityFilter.types, selection)) return;
      const newActivityFeedGroupFilter: ArtistActivityFeedFilterArgs['activityTypes'] =
        selection?.filter(option => option !== ArtistActivityFeedActivityTypeFilterOption.All);
      ArtistActivityState.activityFilter[artistId] = newActivityFeedGroupFilter;
    },
    [artistId, snapshot.activityFilter.types],
  );

  const setUserFilter = useCallback(
    (selection: ArtistActivityFeedTypeFilterOption[]) => {
      if (isEqual(snapshot.activityFilter.types, selection)) return;
      const newActivityFeedGroupFilter: ArtistActivityFeedFilterArgs['types'] = selection?.filter(
        option => option !== ArtistActivityFeedTypeFilterOption.All,
      );
      ArtistActivityState.userFilter[artistId] = newActivityFeedGroupFilter;
    },
    [artistId, snapshot.activityFilter.types],
  );

  const filter: ArtistActivityFeedFilterArgs = {
    activityTypes: snapshot.activityFilter[artistId]?.length
      ? snapshot.activityFilter[artistId]
      : [ArtistActivityFeedActivityTypeFilterOption.All], // Fall back to All if no types are selected
    types: snapshot.userFilter[artistId]?.length
      ? snapshot.userFilter[artistId]
      : [ArtistActivityFeedTypeFilterOption.All], // Fall back to All if no types are selected
  };

  const {
    orderedList: listData,
    isLoadingNewPage: loading,
    isRefetching,
    refetch,
    error,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
  } = useInfiniteQuery(ArtistActivityFeedDocument, {
    getNextPageParam({ data: { artistActivityFeed } }) {
      return (
        artistActivityFeed.pageInfo.hasNextPage && { after: artistActivityFeed.pageInfo.endCursor }
      );
    },
    filterQueryKey: {
      artistId,
      filter,
    },
    variables({ pageParam }) {
      const after = pageParam?.after || null;

      return {
        artistId,
        filter,
        pagination: {
          first: after == null ? 20 : 10,
          after,
          sort: 'DESC' as const,
        },
      };
    },
    list({ artistActivityFeed }) {
      return artistActivityFeed.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    order: [
      [v => v.date, v => v.id],
      ['desc', 'desc'],
    ],
    staleTime: 0,
  });

  const memoizedFooter = useCallback(() => {
    if (!loading && listData.length === 0) return <EmptyState />;
    if (!loading && !hasNextPage) return null;
    return <FeedListSkeleton rows={5} />;
  }, [loading, hasNextPage, listData]);

  // reset the refresh success state
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowRerefreshSuccess(false);
    }, TEMPORARY_SUCCESS_STATE_MS);
    return () => clearTimeout(timeout);
  }, [showRefreshSuccess]);

  const showError = !!error && listData.length === 0 && !loading;

  return (
    <>
      <SectionHeader header="Activity" userWebappUri={webappUri}>
        <Refresh
          showRefreshSuccess={showRefreshSuccess}
          isFetchingNewActivity={isRefetching}
          refetch={refetch}
        />
      </SectionHeader>
      <FeedHeader
        activityFilter={memoizedActivityFilter}
        setActivityFilter={setActivityFilter}
        userFilter={memoizedUserFilter}
        setUserFilter={setUserFilter}
      />
      {!!showError && (
        <ActivityFeed>
          <FeedError divider />
        </ActivityFeed>
      )}

      <ActivityFeed>
        <Virtuoso
          useWindowScroll
          endReached={loadMore}
          data={listData}
          itemContent={(index, item) =>
            renderItemContent({
              index,
              item,
              artistId,
              activityFilter: memoizedActivityFilter,
              userFilter: memoizedUserFilter,
            })
          }
          components={{
            Footer: memoizedFooter,
          }}
          increaseViewportBy={700}
          // Scroll to global state scroll index if popstate event (user used back or forward button in browser)
          // Used to avoid scroll jumping when user switches between tabs
          // Virtuoso breaks if we pass undefined, so we pass an empty object instead
          {...(isPopStateEvent && snapshot.scrollIndices[artistId]
            ? {
                initialTopMostItemIndex: {
                  index: snapshot.scrollIndices[artistId] || 0,
                  align: 'center',
                },
              }
            : {})}
        />
        {/* Render server side feed without virtualized list */}
        {typeof window === 'undefined' &&
          listData.map(item => <ActionComponent key={item.id} item={item} />)}
      </ActivityFeed>
    </>
  );
};
